import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const workflowSteps = [
  { key: 'catalog', icon: FolderIcon, path: '/catalog' },
  { key: 'cohortBuilder', icon: GroupIcon, path: '/olymp/cohort-builder' },
  { key: 'requests', icon: AssignmentIcon, path: '/requests' },
];

export default function DashboardOnboardingDialog({ open, onDismiss }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNavigate = (path) => {
    onDismiss();
    navigate(path);
  };

  return (
    <Dialog
      open={open}
      onClose={onDismiss}
      maxWidth="md"
      fullWidth
      aria-labelledby="dashboard-onboarding-title"
    >
      <DialogTitle id="dashboard-onboarding-title">
        <Stack spacing={1}>
          <Typography variant="h5" fontWeight={700}>
            {t('dashboard.onboarding.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.onboarding.subtitle')}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info" variant="outlined">
            {t('dashboard.onboarding.intro')}
          </Alert>

          <Grid container spacing={2}>
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <Grid item xs={12} md={4} key={step.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      height: '100%',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={index + 1}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                      <Typography variant="h6" fontWeight={700}>
                        {t(`dashboard.onboarding.steps.${step.key}.title`)}
                      </Typography>
                    </Stack>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Icon color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {t(`dashboard.onboarding.steps.${step.key}.description`)}
                      </Typography>
                    </Box>

                    <Button
                      variant="text"
                      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600, mt: 'auto' }}
                      onClick={() => handleNavigate(step.path)}
                    >
                      {t(`dashboard.onboarding.steps.${step.key}.action`)}
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t('dashboard.onboarding.rememberTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.onboarding.rememberText')}
            </Typography>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onDismiss} sx={{ textTransform: 'none' }}>
          {t('dashboard.onboarding.dismiss')}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleNavigate('/catalog')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t('dashboard.onboarding.primaryAction')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
